import React, { useState, useEffect } from 'react';
import { timesheetAPI, userAPI } from '../services/api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import ProjectManagement from './ProjectManagement';
import EmployeeSummary from './EmployeeSummary';
import '../styles/Dashboard.css';

const AdminDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [summaryData, setSummaryData] = useState(null);
  const [pendingTimesheets, setPendingTimesheets] = useState([]);
  const [pastTimesheets, setPastTimesheets] = useState([]);
  const [projectBreakdown, setProjectBreakdown] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedTimesheetForView, setSelectedTimesheetForView] = useState(null);
  const [loadingTimesheet, setLoadingTimesheet] = useState(false);
  const [dateRange, setDateRange] = useState({
    startDate: new Date(new Date().setDate(new Date().getDate() - 30)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [pastTimesheetsFilter, setPastTimesheetsFilter] = useState({
    status: 'all',
    userId: 'all',
    startDate: new Date(new Date().setDate(new Date().getDate() - 90)).toISOString().split('T')[0],
    endDate: new Date().toISOString().split('T')[0]
  });
  const [users, setUsers] = useState([]);

  // needed for employee hour section at top
  // ADD THESE STATE VARIABLES at the top with other useState declarations:
const [selectedEmployeeForChart, setSelectedEmployeeForChart] = useState('');
const [selectedProjects, setSelectedProjects] = useState([]);
const [employeeTimesheetData, setEmployeeTimesheetData] = useState(null);
const [loadingChartData, setLoadingChartData] = useState(false);

// ADD THIS HELPER FUNCTION near the top of your component (after state declarations):
const getTopProjects = (limit = 3) => {
  console.log('projectBreakdown:', projectBreakdown);

  if (!projectBreakdown?.breakdown) {
    console.log('No breakdown found');
    return [];
  }

  // When groupBy is 'project', breakdown keys ARE the project names
  const projects = Object.entries(projectBreakdown.breakdown)
    .map(([projectName, projectData]) => ({
      name: projectName,
      hours: projectData.hours || 0,
      cost: projectData.cost || 0
    }))
    .sort((a, b) => b.hours - a.hours)
    .slice(0, limit);

  console.log('Top projects:', projects);
  return projects;
};
  // ADD THIS HELPER FUNCTION to fetch detailed timesheet data for chart:
const fetchEmployeeChartData = async (userId) => {
  if (!userId) {
    setEmployeeTimesheetData(null);
    return;
  }

  setLoadingChartData(true);
  try {
    const response = await timesheetAPI.getAll({
      userId: userId,
      startDate: dateRange.startDate,
      endDate: dateRange.endDate,
      status: 'approved' // Only show approved timesheets in chart
    });

    // Process timesheets to create time-series data
    const timesheets = response.data;
    const projectMap = new Map();

    timesheets.forEach(timesheet => {
      const weekStart = new Date(timesheet.weekStarting).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      });

      timesheet.entries.forEach(entry => {
        const projectName = entry.project?.name || 'Unknown';
        const projectHours = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
          .reduce((sum, day) => sum + (entry[day] || 0), 0);

        if (!projectMap.has(projectName)) {
          projectMap.set(projectName, []);
        }

        const existingWeek = projectMap.get(projectName).find(w => w.week === weekStart);
        if (existingWeek) {
          existingWeek.hours += projectHours;
        } else {
          projectMap.get(projectName).push({ week: weekStart, hours: projectHours });
        }
      });
    });

    setEmployeeTimesheetData({
      timesheets,
      projectMap,
      projects: Array.from(projectMap.keys())
    });

    // Auto-select all projects initially
    if (selectedProjects.length === 0) {
      setSelectedProjects(Array.from(projectMap.keys()));
    }
  } catch (error) {
    console.error('Error fetching employee chart data:', error);
  } finally {
    setLoadingChartData(false);
  }
};

  useEffect(() => {
    fetchDashboardData();
    fetchUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateRange]);

  useEffect(() => {
    if (selectedEmployeeForChart) {
      fetchEmployeeChartData(selectedEmployeeForChart);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedEmployeeForChart, dateRange]);

  useEffect(() => {
    if (activeTab === 'overview') {
      fetchPastTimesheets();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pastTimesheetsFilter, activeTab]);

  const fetchUsers = async () => {
    try {
      const response = await userAPI.getAll();
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const summaryResponse = await timesheetAPI.getReportSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy: 'user'
      });
      setSummaryData(summaryResponse.data);

      const projectSummaryResponse = await timesheetAPI.getReportSummary({
        startDate: dateRange.startDate,
        endDate: dateRange.endDate,
        groupBy: 'project'
      });

  // console.log('=== FULL API RESPONSE ===');
  // console.log('projectSummaryResponse.data:', projectSummaryResponse.data);
  // console.log('Keys in response:', Object.keys(projectSummaryResponse.data || {}));

// Check breakdown structure
// if (projectSummaryResponse.data?.breakdown) {
//   console.log('=== BREAKDOWN STRUCTURE ===');
//   const firstEmployee = Object.values(projectSummaryResponse.data.breakdown)[0];
//   console.log('First employee data:', firstEmployee);
//   console.log('Keys in first employee:', Object.keys(firstEmployee || {}));
//   console.log('Has projects?:', !!firstEmployee?.projects);

//     if (firstEmployee) {
//       console.log('All employee properties:', firstEmployee);
//     }
//   }


      setProjectBreakdown(projectSummaryResponse.data);

      const pendingResponse = await timesheetAPI.getAll({
        status: 'submitted'
      });
      setPendingTimesheets(pendingResponse.data);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPastTimesheets = async () => {
    try {
      const params = {
        startDate: pastTimesheetsFilter.startDate,
        endDate: pastTimesheetsFilter.endDate
      };

      if (pastTimesheetsFilter.status !== 'all') {
        params.status = pastTimesheetsFilter.status;
      }

      if (pastTimesheetsFilter.userId !== 'all') {
        params.userId = pastTimesheetsFilter.userId;
      }

      const response = await timesheetAPI.getAll(params);
      setPastTimesheets(response.data);
    } catch (error) {
      console.error('Error fetching past timesheets:', error);
    }
  };

  const handleViewTimesheet = async (timesheetId) => {
    setLoadingTimesheet(true);
    try {
      const response = await timesheetAPI.getById(timesheetId);
      setSelectedTimesheetForView(response.data);

      // Smooth scroll to the detail view
      setTimeout(() => {
        const detailElement = document.getElementById('timesheet-detail-view');
        if (detailElement) {
          detailElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      console.error('Error fetching timesheet details:', error);
      alert('Error loading timesheet details');
    } finally {
      setLoadingTimesheet(false);
    }
  };

  const handleReviewTimesheet = async (timesheetId, action, notes = '') => {
    try {
      await timesheetAPI.review(timesheetId, { action, adminNotes: notes });
      fetchDashboardData();
      fetchPastTimesheets();

      if (selectedTimesheetForView && selectedTimesheetForView._id === timesheetId) {
        handleViewTimesheet(timesheetId);
      }

      alert(`Timesheet ${action}ed successfully`);
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to review timesheet'}`);
    }
  };

  const handleUnlockTimesheet = async (timesheetId) => {
    if (!window.confirm('Are you sure you want to unlock this timesheet? The employee will be able to edit it again and it will revert to draft status.')) {
      return;
    }

    try {
      await timesheetAPI.review(timesheetId, { action: 'unlock', adminNotes: 'Unlocked by admin' });
      fetchDashboardData();
      fetchPastTimesheets();

      if (selectedTimesheetForView && selectedTimesheetForView._id === timesheetId) {
        handleViewTimesheet(timesheetId);
      }

      alert('Timesheet unlocked successfully. Status reverted to draft.');
    } catch (error) {
      alert(`Error: ${error.response?.data?.error || 'Failed to unlock timesheet'}`);
    }
  };

  const clearFilters = () => {
    setPastTimesheetsFilter({
      status: 'all',
      userId: 'all',
      startDate: '',
      endDate: ''
    });
  };

  const renderTimesheetDetailView = () => {
    if (!selectedTimesheetForView) return null;

    const timesheet = selectedTimesheetForView;
    const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
    const dayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

    return (
      <div
        id="timesheet-detail-view"
        style={{
          background: 'linear-gradient(135deg, #046d20 0%, #07d83f 100%)',
          borderRadius: '12px',
          padding: '24px',
          marginBottom: '30px',
          boxShadow: '0 8px 16px rgba(0, 0, 0, 0.15)',
          // border: '3px solid #0bf048ff',
          animation: 'fadeIn 0.3s ease-in'
        }}>
        <style>
          {`
            @keyframes fadeIn {
              from { opacity: 0; transform: translateY(-10px); }
              to { opacity: 1; transform: translateY(0); }
            }
          `}
        </style>
        <div style={{
          background: 'white',
          borderRadius: '8px',
          padding: '24px'
        }}>
          {/* Header */}
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            marginBottom: '24px',
            paddingBottom: '16px',
            borderBottom: '2px solid #e0e0e0'
          }}>
            <div>
              <h3 style={{ margin: '0 0 8px 0', color: '#333', fontSize: '1.5rem' }}>
                📋 Timesheet Details
              </h3>
              <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap', marginTop: '12px' }}>
                <div>
                  <span style={{ fontWeight: '600', color: '#666' }}>Employee: </span>
                  <span style={{ color: '#333', fontSize: '1.1rem' }}>
                    {timesheet.user.firstName} {timesheet.user.lastName}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#666' }}>Week: </span>
                  <span style={{ color: '#333' }}>
                    {new Date(timesheet.weekStarting).toLocaleDateString()} - {new Date(timesheet.weekEnding).toLocaleDateString()}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#666' }}>Status: </span>
                  <span className={`status-badge status-${timesheet.status}`}>
                    {timesheet.status}
                  </span>
                </div>
                <div>
                  <span style={{ fontWeight: '600', color: '#666' }}>Total Hours: </span>
                  <span style={{ color: '#28a745', fontWeight: 'bold', fontSize: '1.1rem' }}>
                    {timesheet.totalHours || 0}
                  </span>
                </div>
              </div>
            </div>
            <button
              onClick={() => setSelectedTimesheetForView(null)}
              style={{
                background: '#f44336',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                padding: '8px 16px',
                cursor: 'pointer',
                fontWeight: '600',
                fontSize: '14px',
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => e.target.style.background = '#d32f2f'}
              onMouseOut={(e) => e.target.style.background = '#f44336'}
            >
              ✕ Clear
            </button>
          </div>

          {/* Timesheet Entries Table */}
          {timesheet.entries && timesheet.entries.length > 0 ? (
            <div style={{ overflowX: 'auto' }}>
              <table className="timesheet-table" style={{ marginBottom: '0' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', minWidth: '200px' }}>Project</th>
                    {dayLabels.map((day, idx) => (
                      <th key={idx}>{day}</th>
                    ))}
                    <th>Total</th>
                    <th style={{ textAlign: 'left', minWidth: '150px' }}>Notes</th>
                  </tr>
                </thead>
                <tbody>
                  {timesheet.entries.map((entry, idx) => {
                    const rowTotal = days.reduce((sum, day) => sum + (entry[day] || 0), 0);
                    return (
                      <tr key={idx}>
                        <td style={{ textAlign: 'left', fontWeight: '600', color: '#333' }}>
                          {entry.project?.name || 'Unknown Project'}
                        </td>
                        {days.map((day, dayIdx) => (
                          <td key={dayIdx} style={{
                            background: entry[day] > 0 ? '#e8f5e9' : 'transparent',
                            fontWeight: entry[day] > 0 ? '600' : 'normal'
                          }}>
                            {entry[day] || '-'}
                          </td>
                        ))}
                        <td className="total-cell" style={{
                          background: '#667eea',
                          color: 'white',
                          fontWeight: 'bold'
                        }}>
                          {rowTotal.toFixed(2)}
                        </td>
                        <td style={{ textAlign: 'left', fontSize: '0.9rem', color: '#666' }}>
                          {entry.notes || '-'}
                        </td>
                      </tr>
                    );
                  })}
                  <tr className="totals-row">
                    <td style={{ fontWeight: 'bold' }}>Daily Totals</td>
                    {days.map((day, dayIdx) => {
                      const dayTotal = timesheet.entries.reduce((sum, entry) => sum + (entry[day] || 0), 0);
                      return (
                        <td key={dayIdx} style={{
                          fontWeight: 'bold',
                          background: dayTotal > 0 ? '#f5f5f5' : 'transparent'
                        }}>
                          {dayTotal.toFixed(2)}
                        </td>
                      );
                    })}
                    <td style={{
                      background: '#28a745',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '1.1rem'
                    }}>
                      {timesheet.totalHours || 0}
                    </td>
                    <td></td>
                  </tr>
                </tbody>
              </table>
            </div>
          ) : (
            <div className="no-data">No entries in this timesheet</div>
          )}

          {/* Admin Notes */}
          {timesheet.adminNotes && (
            <div style={{
              marginTop: '20px',
              padding: '16px',
              background: '#fff3cd',
              borderLeft: '4px solid #ffc107',
              borderRadius: '4px'
            }}>
              <div style={{ fontWeight: '600', color: '#856404', marginBottom: '8px' }}>
                📝 Admin Notes:
              </div>
              <div style={{ color: '#856404' }}>
                {timesheet.adminNotes}
              </div>
            </div>
          )}

          {/* Approval Info */}
          {timesheet.approvedBy && (
            <div style={{
              marginTop: '16px',
              padding: '12px',
              background: '#e8f5e9',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#2e7d32'
            }}>
              <strong>Approved by:</strong> {timesheet.approvedBy.firstName} {timesheet.approvedBy.lastName}
              {timesheet.approvedAt && (
                <span style={{ marginLeft: '16px' }}>
                  <strong>on</strong> {new Date(timesheet.approvedAt).toLocaleString()}
                </span>
              )}
            </div>
          )}

          {/* Submitted Info */}
          {timesheet.submittedAt && (
            <div style={{
              marginTop: '12px',
              padding: '12px',
              background: '#fff3e0',
              borderRadius: '4px',
              fontSize: '0.9rem',
              color: '#e65100'
            }}>
              <strong>Submitted on:</strong> {new Date(timesheet.submittedAt).toLocaleString()}
            </div>
          )}

          {/* Action Buttons */}
          <div style={{
            marginTop: '20px',
            display: 'flex',
            gap: '10px',
            paddingTop: '16px',
            borderTop: '1px solid #e0e0e0',
            flexWrap: 'wrap'
          }}>
            {timesheet.status === 'submitted' && (
              <>
                <button
                  className="btn btn-success"
                  onClick={() => handleReviewTimesheet(timesheet._id, 'approve')}
                >
                  ✓ Approve
                </button>
                <button
                  className="btn btn-danger"
                  onClick={() => {
                    const notes = prompt('Reason for rejection:');
                    if (notes) handleReviewTimesheet(timesheet._id, 'reject', notes);
                  }}
                >
                  ✗ Reject
                </button>
              </>
            )}
            {(timesheet.status === 'submitted' || timesheet.status === 'approved') && (
              <button
                className="btn btn-warning"
                onClick={() => handleUnlockTimesheet(timesheet._id)}
              >
                🔓 Unlock
              </button>
            )}
            <button
              className="btn btn-info"
              onClick={() => window.open(`/timesheet/${timesheet._id}`, '_blank')}
            >
              🔗 Open in New Tab
            </button>
          </div>
        </div>
      </div>
    );
  };

  const renderPastTimesheets = () => (
    <div className="past-timesheets-section">
      <div className="section-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3>Past Timesheets</h3>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-secondary btn-sm"
            onClick={clearFilters}
          >
            Clear Filters
          </button>
          <button
            className="btn btn-info btn-sm"
            onClick={() => window.open('/timesheets', '_blank')}
          >
            View All Timesheets
          </button>
        </div>
      </div>

      <div className="filters-row" style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
        gap: '15px',
        marginBottom: '20px',
        padding: '15px',
        backgroundColor: '#f5f5f5',
        borderRadius: '8px'
      }}>
        <div className="filter-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Employee:</label>
          <select
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            value={pastTimesheetsFilter.userId}
            onChange={(e) => setPastTimesheetsFilter({...pastTimesheetsFilter, userId: e.target.value})}
          >
            <option value="all">All Employees</option>
            {users.map(user => (
              <option key={user._id} value={user._id}>
                {user.firstName} {user.lastName}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Status:</label>
          <select
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            value={pastTimesheetsFilter.status}
            onChange={(e) => setPastTimesheetsFilter({...pastTimesheetsFilter, status: e.target.value})}
          >
            <option value="all">All Statuses</option>
            <option value="draft">Draft</option>
            <option value="submitted">Submitted</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
          </select>
        </div>
        <br></br>
        <div className="filter-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>Start Date:</label>
          <input
            type="date"
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            value={pastTimesheetsFilter.startDate}
            onChange={(e) => setPastTimesheetsFilter({...pastTimesheetsFilter, startDate: e.target.value})}
          />
        </div>

        <div className="filter-group">
          <label style={{ fontWeight: 'bold', display: 'block', marginBottom: '5px' }}>End Date:</label>
          <input
            type="date"
            style={{ width: '100%', padding: '8px', borderRadius: '4px', border: '1px solid #ddd' }}
            value={pastTimesheetsFilter.endDate}
            onChange={(e) => setPastTimesheetsFilter({...pastTimesheetsFilter, endDate: e.target.value})}
          />
        </div>
      </div>

      {pastTimesheets.length > 0 ? (
        <>
          <div style={{ marginBottom: '10px', color: '#666' }}>
            Showing {pastTimesheets.length} timesheet{pastTimesheets.length !== 1 ? 's' : ''}
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Employee</th>
                  <th>Week Starting</th>
                  <th>Total Hours</th>
                  <th>Status</th>
                  <th>Submitted Date</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pastTimesheets.map(timesheet => (
                  <tr
                    key={timesheet._id}
                    style={{
                      background: selectedTimesheetForView?._id === timesheet._id ? '#e3f2fd' : 'transparent',
                      transition: 'background 0.2s'
                    }}
                  >
                    <td>{timesheet.user.firstName} {timesheet.user.lastName}</td>
                    <td>{new Date(timesheet.weekStarting).toLocaleDateString()}</td>
                    <td>{timesheet.totalHours}</td>
                    <td>
                      <span className={`status-badge status-${timesheet.status}`}>
                        {timesheet.status}
                      </span>
                    </td>
                    <td>
                      {timesheet.submittedAt
                        ? new Date(timesheet.submittedAt).toLocaleDateString()
                        : 'N/A'
                      }
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                        <button
                          className="btn btn-info btn-sm"
                          onClick={() => handleViewTimesheet(timesheet._id)}
                          style={{
                            fontWeight: selectedTimesheetForView?._id === timesheet._id ? 'bold' : 'normal',
                            background: selectedTimesheetForView?._id === timesheet._id ? '#0d8ba5' : '#17a2b8'
                          }}
                        >
                          {selectedTimesheetForView?._id === timesheet._id ? '👁 Viewing' : '👁 View'}
                        </button>
                        {timesheet.status === 'submitted' && (
                          <>
                            <button
                              className="btn btn-success btn-sm"
                              onClick={() => handleReviewTimesheet(timesheet._id, 'approve')}
                            >
                              Approve
                            </button>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => {
                                const notes = prompt('Reason for rejection:');
                                if (notes) handleReviewTimesheet(timesheet._id, 'reject', notes);
                              }}
                            >
                              Reject
                            </button>
                          </>
                        )}
                        {(timesheet.status === 'submitted' || timesheet.status === 'approved') && (
                          <button
                            className="btn btn-warning btn-sm"
                            onClick={() => handleUnlockTimesheet(timesheet._id)}
                            title="Unlock and revert to draft"
                          >
                            Unlock
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : (
        <div className="no-data" style={{ padding: '40px', textAlign: 'center', color: '#666' }}>
          No timesheets found for the selected filters. Try adjusting your filters or clearing them.
        </div>
      )}
    </div>
  );

const renderOverview = () => (
  <div className="overview-section">
    {/* Stats Cards - Top Projects, Pending, Active Employees */}
    <div className="stats-cards-green">
      {/* Top 3 Projects by Hours */}
      {(() => {
        const topProjects = getTopProjects(3);
        return topProjects.length > 0 ? (
          topProjects.map((project, idx) => (
            <div
              key={project.name}
              className={`project-card project-card-rank-${idx + 1}`}
            >
              <div className="project-card-badge">
                #{idx + 1}
              </div>

              <div className="project-card-label">
                {idx === 0 ? '🥇 Top Project' : idx === 1 ? '🥈 2nd Project' : '🥉 3rd Project'}
              </div>

              <div className="project-card-name">
                {project.name}
              </div>

              <div className="project-card-stats">
                <div className="project-card-stat">
                  <div className="project-card-stat-label">
                    Total Hours
                  </div>
                  <div className="project-card-stat-value-hours">
                    {project.hours.toFixed(1)}
                  </div>
                </div>
                <div className="project-card-stat project-card-stat-right">
                  <div className="project-card-stat-label">
                    Total Cost
                  </div>
                  <div className="project-card-stat-value-cost">
                    ${project.cost.toFixed(0)}
                  </div>
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="no-data-card">
            <div className="no-data-icon">📊</div>
            <div>No project data available</div>
          </div>
        );
      })()}

      {/* Pending Reviews Card */}
      <div className="pending-card">
        {pendingTimesheets.length > 0 && (
          <div className={`pending-card-badge ${pendingTimesheets.length > 5 ? 'pending-card-badge-pulse' : ''}`}>
            ACTION NEEDED
          </div>
        )}

        <div className="pending-card-label">
          ⏳ Pending Reviews
        </div>

        <div className={`pending-card-value ${pendingTimesheets.length > 0 ? 'pending-card-value-warning' : 'pending-card-value-success'}`}>
          {pendingTimesheets.length}
        </div>

        <div className="pending-card-footer">
          {pendingTimesheets.length === 0
            ? '✓ All caught up!'
            : `${pendingTimesheets.length} timesheet${pendingTimesheets.length !== 1 ? 's' : ''} awaiting approval`}
        </div>
      </div>

      {/* Active Employees Card */}
      <div className="active-employees-card">
        <div className="active-employees-card-decoration" />

        <div className="active-employees-card-label">
          👥 Active Employees
        </div>

        <div className="active-employees-card-value">
          {Object.keys(summaryData?.breakdown || {}).length}
        </div>

        <div className="active-employees-card-footer">
          {(() => {
            const totalHours = summaryData?.totals?.hours || 0;
            const employeeCount = Object.keys(summaryData?.breakdown || {}).length;
            const avgHours = employeeCount > 0 ? (totalHours / employeeCount).toFixed(1) : 0;
            return `Avg ${avgHours} hrs/employee`;
          })()}
        </div>
      </div>
    </div>

    {/* Employee Hours Visualization */}
    <div className="employee-hours-section">
      <h3>📊 Employee Hours Visualization</h3>

      <div style={{
        display: 'grid',
        gridTemplateColumns: '400px 1fr',
        gap: '30px',
        marginTop: '20px'
      }}>
        {/* LEFT SIDE - Controls and Summary */}
        <div style={{
          background: 'white',
          padding: '20px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          height: 'fit-content'
        }}>
          {/* Date Range */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '8px',
              fontSize: '0.9rem'
            }}>
              📅 Date Range
            </label>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <input
                type="date"
                value={dateRange.startDate}
                onChange={(e) => setDateRange({...dateRange, startDate: e.target.value})}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
              <span style={{ color: '#666', fontWeight: '600' }}>to</span>
              <input
                type="date"
                value={dateRange.endDate}
                onChange={(e) => setDateRange({...dateRange, endDate: e.target.value})}
                style={{
                  flex: 1,
                  padding: '8px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '0.9rem'
                }}
              />
            </div>
          </div>

          {/* Employee Selection */}
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'block',
              fontWeight: '600',
              color: '#333',
              marginBottom: '8px',
              fontSize: '0.9rem'
            }}>
              👤 Select Employee
            </label>
            <select
              value={selectedEmployeeForChart}
              onChange={(e) => {
                setSelectedEmployeeForChart(e.target.value);
                setSelectedProjects([]);
              }}
              style={{
                width: '100%',
                padding: '10px',
                border: '2px solid #667eea',
                borderRadius: '6px',
                fontSize: '0.95rem',
                fontWeight: '500',
                cursor: 'pointer',
                background: selectedEmployeeForChart ? 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)' : 'white'
              }}
            >
              <option value="">-- Select an Employee --</option>
              {users.map(user => (
                <option key={user._id} value={user._id}>
                  {user.firstName} {user.lastName}
                </option>
              ))}
            </select>
          </div>

          {/* Project Selection */}
          {selectedEmployeeForChart && employeeTimesheetData && (
            <div style={{ marginBottom: '20px' }}>
              <label style={{
                display: 'block',
                fontWeight: '600',
                color: '#333',
                marginBottom: '8px',
                fontSize: '0.9rem'
              }}>
                📁 Select Projects
              </label>
              <div style={{
                border: '2px solid #ddd',
                borderRadius: '6px',
                padding: '10px',
                maxHeight: '200px',
                overflowY: 'auto',
                background: '#f8f9fa'
              }}>
                {employeeTimesheetData.projects.length > 0 ? (
                  <>
                    <div style={{
                      marginBottom: '10px',
                      paddingBottom: '10px',
                      borderBottom: '1px solid #ddd',
                      display: 'flex',
                      gap: '8px'
                    }}>
                      <button
                        onClick={() => setSelectedProjects(employeeTimesheetData.projects)}
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: '#667eea',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Select All
                      </button>
                      <button
                        onClick={() => setSelectedProjects([])}
                        style={{
                          flex: 1,
                          padding: '6px',
                          background: '#6c757d',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '0.85rem',
                          cursor: 'pointer',
                          fontWeight: '600'
                        }}
                      >
                        Clear All
                      </button>
                    </div>
                    {employeeTimesheetData.projects.map((project) => (
                      <label
                        key={project}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          padding: '8px',
                          marginBottom: '4px',
                          background: selectedProjects.includes(project) ? '#e3f2fd' : 'white',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          transition: 'all 0.2s',
                          border: selectedProjects.includes(project) ? '2px solid #667eea' : '1px solid #ddd'
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={selectedProjects.includes(project)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedProjects([...selectedProjects, project]);
                            } else {
                              setSelectedProjects(selectedProjects.filter(p => p !== project));
                            }
                          }}
                          style={{ marginRight: '10px', cursor: 'pointer', width: '16px', height: '16px' }}
                        />
                        <span style={{
                          flex: 1,
                          fontSize: '0.9rem',
                          fontWeight: selectedProjects.includes(project) ? '600' : 'normal'
                        }}>
                          {project}
                        </span>
                        <span style={{
                          fontSize: '0.85rem',
                          fontWeight: '600',
                          color: '#667eea',
                          background: '#667eea15',
                          padding: '2px 8px',
                          borderRadius: '12px'
                        }}>
                          {employeeTimesheetData.projectMap.get(project)
                            .reduce((sum, week) => sum + week.hours, 0).toFixed(1)}h
                        </span>
                      </label>
                    ))}
                  </>
                ) : (
                  <div style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                    No projects found for this employee in the selected date range
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Summary Stats */}
          {selectedEmployeeForChart && employeeTimesheetData && selectedProjects.length > 0 && (
            <div style={{ // gradient green
              background: 'linear-gradient(135deg, #046d20 0%, #07d83f 100%)',
              padding: '20px',
              borderRadius: '8px',
              color: 'white'
            }}>
              <div style={{ fontSize: '0.9rem', opacity: 0.9, marginBottom: '8px' }}>
                Total Hours
              </div>
              <div style={{ fontSize: '2.5rem', fontWeight: 'bold', marginBottom: '4px' }}>
                {selectedProjects.reduce((total, projectName) => {
                  const projectData = employeeTimesheetData.projectMap.get(projectName) || [];
                  return total + projectData.reduce((sum, week) => sum + week.hours, 0);
                }, 0).toFixed(1)}
              </div>
              <div style={{ fontSize: '0.85rem', opacity: 0.8 }}>
                Across {selectedProjects.length} project{selectedProjects.length !== 1 ? 's' : ''}
              </div>
              <div style={{
                marginTop: '12px',
                paddingTop: '12px',
                borderTop: '1px solid rgba(255,255,255,0.3)',
                fontSize: '0.85rem'
              }}>
                {new Date(dateRange.startDate).toLocaleDateString()} - {new Date(dateRange.endDate).toLocaleDateString()}
              </div>
            </div>
          )}
        </div>

        {/* RIGHT SIDE - Chart */}
        <div style={{
          background: 'white',
          padding: '24px',
          borderRadius: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          minHeight: '400px',
          display: 'flex',
          flexDirection: 'column'
        }}>
          {!selectedEmployeeForChart ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '20px' }}>📈</div>
              <div style={{ fontSize: '1.2rem', fontWeight: '600', marginBottom: '8px' }}>
                Select an Employee
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                Choose an employee from the dropdown to view their hours by project over time
              </div>
            </div>
          ) : loadingChartData ? (
            <div style={{
              flex: 1,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666'
            }}>
              <div style={{ textAlign: 'center' }}>
                <div style={{ fontSize: '2rem', marginBottom: '10px' }}>⏳</div>
                <div>Loading chart data...</div>
              </div>
            </div>
          ) : !employeeTimesheetData || employeeTimesheetData.projects.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>📭</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                No Data Available
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                This employee has no approved timesheets in the selected date range
              </div>
            </div>
          ) : selectedProjects.length === 0 ? (
            <div style={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#999',
              textAlign: 'center'
            }}>
              <div style={{ fontSize: '3rem', marginBottom: '20px' }}>☑️</div>
              <div style={{ fontSize: '1.1rem', fontWeight: '600', marginBottom: '8px' }}>
                Select Projects
              </div>
              <div style={{ fontSize: '0.9rem' }}>
                Choose one or more projects to visualize hours over time
              </div>
            </div>
          ) : (
            <>
              <div style={{ marginBottom: '20px' }}>
                <h4 style={{ margin: '0 0 4px 0', color: '#333', fontSize: '1.2rem' }}>
                  Hours by Week
                </h4>
                <div style={{ fontSize: '0.85rem', color: '#666' }}>
                  {users.find(u => u._id === selectedEmployeeForChart)?.firstName} {users.find(u => u._id === selectedEmployeeForChart)?.lastName}
                </div>
              </div>

              {/* Chart Implementation using Recharts */}
              {(() => {
                // Prepare data for chart - combine all weeks across selected projects
                const allWeeks = new Set();
                selectedProjects.forEach(projectName => {
                  const projectData = employeeTimesheetData.projectMap.get(projectName) || [];
                  projectData.forEach(weekData => allWeeks.add(weekData.week));
                });

                const sortedWeeks = Array.from(allWeeks).sort((a, b) => {
                  const dateA = new Date(a + ' 2024');
                  const dateB = new Date(b + ' 2024');
                  return dateA - dateB;
                });

                const chartData = sortedWeeks.map(week => {
                  const dataPoint = { week };
                  selectedProjects.forEach(projectName => {
                    const projectData = employeeTimesheetData.projectMap.get(projectName) || [];
                    const weekData = projectData.find(w => w.week === week);
                    dataPoint[projectName] = weekData ? weekData.hours : 0;
                  });
                  return dataPoint;
                });

                const colors = ['#667eea', '#f6ad55', '#48bb78', '#ed64a6', '#4299e1', '#ecc94b', '#9f7aea'];

                return (
                  <ResponsiveContainer width="100%" height={400}>
                    <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis
                        dataKey="week"
                        stroke="#666"
                        style={{ fontSize: '0.85rem' }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis
                        stroke="#666"
                        style={{ fontSize: '0.85rem' }}
                        label={{ value: 'Hours', angle: -90, position: 'insideLeft', style: { fontSize: '0.9rem', fill: '#666' } }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: 'white',
                          border: '2px solid #667eea',
                          borderRadius: '8px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
                        }}
                        labelStyle={{ fontWeight: '600', color: '#333' }}
                      />
                      <Legend
                        wrapperStyle={{ paddingTop: '20px' }}
                        iconType="line"
                      />
                      {selectedProjects.map((projectName, idx) => (
                        <Line
                          key={projectName}
                          type="monotone"
                          dataKey={projectName}
                          stroke={colors[idx % colors.length]}
                          strokeWidth={3}
                          dot={{ fill: colors[idx % colors.length], strokeWidth: 2, r: 5 }}
                          activeDot={{ r: 7 }}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                );
              })()}
            </>
          )}
        </div>
      </div>
    </div>

    {/* Pending Timesheet Reviews */}
    {pendingTimesheets.length > 0 && (
      <div className="pending-section">
        <h3>Pending Timesheet Reviews</h3>
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Week Starting</th>
              <th>Total Hours</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pendingTimesheets.map(timesheet => (
              <tr
                key={timesheet._id}
                style={{
                  background: selectedTimesheetForView?._id === timesheet._id ? '#e3f2fd' : 'transparent',
                  transition: 'background 0.2s'
                }}
              >
                <td>{timesheet.user.firstName} {timesheet.user.lastName}</td>
                <td>{new Date(timesheet.weekStarting).toLocaleDateString()}</td>
                <td>{timesheet.totalHours}</td>
                <td>
                  <div style={{ display: 'flex', gap: '5px', flexWrap: 'wrap' }}>
                    <button
                      className="btn btn-info btn-sm"
                      onClick={() => handleViewTimesheet(timesheet._id)}
                      style={{
                        fontWeight: selectedTimesheetForView?._id === timesheet._id ? 'bold' : 'normal',
                        background: selectedTimesheetForView?._id === timesheet._id ? '#0d8ba5' : '#17a2b8'
                      }}
                    >
                      {selectedTimesheetForView?._id === timesheet._id ? '👁 Viewing' : '👁 View'}
                    </button>
                    <button
                      className="btn btn-success btn-sm"
                      onClick={() => handleReviewTimesheet(timesheet._id, 'approve')}
                    >
                      Approve
                    </button>
                    <button
                      className="btn btn-danger btn-sm"
                      onClick={() => {
                        const notes = prompt('Reason for rejection:');
                        if (notes) handleReviewTimesheet(timesheet._id, 'reject', notes);
                      }}
                    >
                      Reject
                    </button>
                    <button
                      className="btn btn-warning btn-sm"
                      onClick={() => handleUnlockTimesheet(timesheet._id)}
                      title="Unlock and revert to draft"
                    >
                      Unlock
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )}

    {/* Timesheet Detail View */}
    {loadingTimesheet ? (
      <div style={{
        textAlign: 'center',
        padding: '40px',
        background: '#f8f9fa',
        borderRadius: '8px',
        margin: '30px 0'
      }}>
        <div style={{ fontSize: '1.2rem', color: '#666' }}>⏳ Loading timesheet details...</div>
      </div>
    ) : selectedTimesheetForView && renderTimesheetDetailView()}

    {/* Past Timesheets */}
    {renderPastTimesheets()}
  </div>
);

  return (
    <div className="admin-dashboard">
      <h2>Admin Dashboard</h2>

      <div className="tabs">
        <button
          className={`tab ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button
          className={`tab ${activeTab === 'projects' ? 'active' : ''}`}
          onClick={() => setActiveTab('projects')}
        >
          Projects
        </button>
        <button
          className={`tab ${activeTab === 'employees' ? 'active' : ''}`}
          onClick={() => setActiveTab('employees')}
        >
          Employees
        </button>
      </div>

      <div className="tab-content">
        {loading ? (
          <div className="loading">Loading...</div>
        ) : (
          <>
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'projects' && <ProjectManagement />}
            {activeTab === 'employees' && <EmployeeSummary />}
          </>
        )}
      </div>
    </div>
  );
};

export default AdminDashboard;
